import { NavLink, useNavigate } from "react-router-dom";
import { Home, CalendarDays, MessageCircle, TrendingUp, Moon, Sun, LogOut, User } from "lucide-react";
import { Logo } from "./Logo";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/today", label: "Today", icon: Home, id: "today" },
  { to: "/plan", label: "Plan", icon: CalendarDays, id: "plan" },
  { to: "/coach", label: "Coach", icon: MessageCircle, id: "coach" },
  { to: "/progress", label: "Progress", icon: TrendingUp, id: "progress" },
];

function ThemeButton() {
  const { theme, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle"
      className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
      aria-label="Toggle theme"
    >
      {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
    </button>
  );
}

export default function AppShell({ children }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/auth");
  };

  return (
    <div className="min-h-screen caden-grain bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-border bg-card/40 backdrop-blur-sm z-20 px-5 py-6">
        <Logo size={38} />
        <nav className="mt-10 flex flex-col gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.id}
              to={n.to}
              data-testid={`nav-${n.id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`
              }
            >
              <n.icon size={20} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto flex items-center justify-between border-t border-border pt-4">
          <button onClick={() => navigate("/profile")} data-testid="nav-profile" className="flex items-center gap-2 min-w-0 hover:text-foreground text-muted-foreground transition-colors">
            <div className="brand-gradient w-8 h-8 rounded-lg flex items-center justify-center font-heading font-black text-white text-sm shrink-0">
              {(user?.name || "A").charAt(0).toUpperCase()}
            </div>
            <span className="text-sm truncate max-w-[110px]">{user?.name}</span>
          </button>
          <div className="flex items-center gap-1">
            <ThemeButton />
            <button onClick={handleLogout} data-testid="logout-btn" className="p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-20 flex items-center justify-between px-4 h-16 border-b border-border bg-background/80 backdrop-blur-md">
        <Logo size={32} />
        <div className="flex items-center gap-1">
          <ThemeButton />
          <button onClick={() => navigate("/profile")} data-testid="nav-profile-mobile" className="brand-gradient w-8 h-8 rounded-lg flex items-center justify-center font-heading font-black text-white text-sm">
            {(user?.name || "A").charAt(0).toUpperCase()}
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="md:pl-64 relative z-10 pb-24 md:pb-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 md:pt-10">{children}</div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-20 border-t border-border bg-background/90 backdrop-blur-md flex justify-around px-2 py-2">
        {NAV.map((n) => (
          <NavLink
            key={n.id}
            to={n.to}
            data-testid={`nav-mobile-${n.id}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            }
          >
            <n.icon size={22} />
            {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
